import { type RefObject, useEffect } from 'react';
import { useIntl } from 'react-intl';
import type { OnfidoModes } from './onfido.types';

let lastInnerText: string;

type Step = 'first' | 'other' | 'last';

export function useOnfidoObserver(
  ref: RefObject<HTMLElement>,
  mode: OnfidoModes,
  isLoaded: boolean,
  onStepChange?: (step: Step) => void
): void {
  const intl = useIntl();

  useEffect(() => {
    if (!isLoaded) {
      return;
    }

    const element = ref.current;
    if (!element) {
      return;
    }

    const mutationObserver = new MutationObserver(() => {
      if (mode !== 'onfido_standalone') {
        return;
      }

      const titleElement = element.querySelector<HTMLElement>('.onfido-sdk-ui-PageTitle-titleSpan');

      let stepChanged = false;

      if (titleElement) {
        if (titleElement.innerText !== lastInnerText) {
          stepChanged = true;
          lastInnerText = titleElement.innerText;
        }
      }

      const isFirstStep = titleElement?.innerText.includes(
        intl.formatMessage({
          // oxlint-disable-next-line @repo/no-dynamic-intl-id
          id: `identity-verification-kit.qonto-onfido.${mode}.desktop.welcome.title`,
        })
      );

      const isLastStep = titleElement?.innerText.includes(
        intl.formatMessage({
          // oxlint-disable-next-line @repo/no-dynamic-intl-id
          id: `identity-verification-kit.qonto-onfido.${mode}.desktop.outro.title`,
        })
      );

      if (stepChanged) {
        const getStep = (): Step => {
          if (isFirstStep) {
            return 'first';
          }

          if (isLastStep) {
            return 'last';
          }

          return 'other';
        };

        onStepChange?.(getStep());
      }
    });

    mutationObserver.observe(element, {
      attributes: true,
      childList: true,
      subtree: true,
    });

    return () => {
      mutationObserver.disconnect();
    };
  }, [ref, intl, mode, onStepChange, isLoaded]);
}
